import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Field, change, getFormValues } from "redux-form";

import classNames from "classnames";
import { Icon, ReduxControls, Tooltip } from "rhombus-react";

import { fetchAssetDetail } from 'reducers/Assets';
import { setActiveVariable, updateCropMetadata } from "reducers/Refinements";

import useCropMetadata from "hooks/useCropMetadata";
import { formatBytes, useClickOutsideHandler, useModalStack } from "utils/common";
import { ASSET_TYPES } from "utils/constants";

import { EDIT_VERSION_BLADE_FORM } from "widgets/forms/list";
import AdjustFramingBlade from "widgets/modals/versions/refinement/AdjustFramingBlade";
import SelectAssets from "widgets/modals/assets/SelectAssets";
import FramingButton from "./refinement/FramingButton";

import {
	getModalProps,
	getSelectProps,
	mapAssetToVersionVariable,
	sanitizeVariableFieldName
} from './Helpers';

import IconCell from "./IconCell";
import VariableInfoCell from "./VariableInfoCell";

import "./AssetEditor.css";


export default function AssetEditor({
	libraryId,
	versionInfo,
	search,
	value,
	videoEncodingFilter,
	accept,
	saveVersion,
	saveAsVersion,
	showFilesize = true,
	form,
	showEditButton = false,
}) {
	const dispatch = useDispatch()
		, [isEditing, setIsEditing] = useState(false)
		, [isSearching, setIsSearching] = useState(false)
		, values = useSelector(getFormValues(EDIT_VERSION_BLADE_FORM)) || {}
		, { name, data, filesize, type, cropMetadata, assetId } = value
		, fieldName = sanitizeVariableFieldName(name)
		, editInputName = `${fieldName}Edit`
		, [rowRef] = useClickOutsideHandler(stopEditing)
		, {
			canvas,
			frame,
			isAutoSmartFramable } = cropMetadata || {}
		, { width: frameWidth, height: frameHeight } = frame || {}
		, commonModalProps = getModalProps({ closeModal, assetType: type })
		// eslint-disable-next-line no-unused-vars
		, [modalStack, { pushModal, clearStack }, ModalStackHead] = useModalStack({ commonModalProps })
		, isVideo = type === ASSET_TYPES.VIDEO
		, isImage = type === ASSET_TYPES.IMAGE
		, extraSelectProps = { onBlur: stopEditing }
		, selectProps = getSelectProps({ extraSelectProps, search, type, videoEncodingFilter, accept })
		, shouldAutocrop = isAutoSmartFramable && frameWidth && (!canvas || !canvas?.width) // If we have frame but no canvas
		, { isLoading, cropMetadata: autoCropMetaData } = useCropMetadata({
			assetId,
			frameWidth,
			frameHeight,
			// We want to skip expensive call to adStusio when we do not need it
			skip: !shouldAutocrop,
		});

	let showAdjustFramingModal = false;

	if(isImage && frameWidth && frameHeight){
		showAdjustFramingModal = true;
	}

	if (data.match(/\.svg$/)) {
		showAdjustFramingModal = false;
	}

	function startEditing() {
		setIsEditing(true);
		setIsSearching(false);
	}
	function startSearching() {
		setIsEditing(true);
		setIsSearching(true);
	}
	function stopEditing() {
		setIsEditing(false);
		setIsSearching(false);
	}

	function closeModal() { clearStack(); }

	useEffect(() => {
		if (!isLoading && autoCropMetaData?.canvas?.width) {
			dispatch(updateCropMetadata(name, values, autoCropMetaData.canvas, false));
		}
	}, [autoCropMetaData, isLoading, name]);

	useEffect(() => {
		if (isEditing && !isSearching) {
			forceEditInputValue();
		}
	}, [editInputName, isEditing, isSearching, value]);

	function forceEditInputValue(newValue = value) {
		dispatch(change(form, editInputName, newValue.data || ""));
	}

	function selectFromModal([assetId], assets) {
		let selectedAsset = assets.find(asset => asset.id === assetId);

		if (!selectedAsset) {
			// This generally shouldn't happen because we flush selections when the Asset items change
			console.error("Could not update asset selection.  Asset not found.");
			return;
		}

		/*
			When we select an Asset from SelectAssets, we have to map it to the Version Variable format.

			Variables have a `prop` that maps to the Asset's `url` property, but `url` isn't included
			in data from the combined Assets endpoint. We have to get it from the type-specific Asset detail GET.

			It's not displayed in the AssetEditor, but it's required by some legacy API code when an image
			variable is updated on an approved creative.
		*/
		if (selectedAsset.assetType === ASSET_TYPES.IMAGE) {
			dispatch(fetchAssetDetail(libraryId, selectedAsset.assetType, selectedAsset.id))
				.then((res) => {
					if (!res?.success) { return; }

					const { url: prop = "", width, height } = res?.payload?.data || {};
					let newValue = mapAssetToVersionVariable(name, selectedAsset, prop);
					if (cropMetadata) {
						newValue = {
							...newValue,
							cropMetadata: {
								...cropMetadata,
								hasManualRefinements: undefined,
								asset: { width, height },
								canvas: undefined,
							}, // Preserve crop metadata for frame and add asset dimensions
						};
					}
					dispatch(change(form, fieldName, newValue));
				});
			return;
		}

		let newValue = mapAssetToVersionVariable(name, selectedAsset);
		dispatch(change(form, fieldName, newValue));
	}

	const openSelectionModal = () => {
		function Configured(props) {
			let selectAssetProps = {
				...props,
				type: type.toLowerCase(),
				onSelect: selectFromModal,
				accept,
			};

			if (videoEncodingFilter === 'false') {
				selectAssetProps.allowEncodedVideo = false;
			} else if (videoEncodingFilter === 'true') {
				selectAssetProps.allowUnencodedVideo = false;
			}

			return <SelectAssets {...selectAssetProps} />;
		}
		pushModal(Configured);
	};

	const openAdjustFramingModal = () => {
		// Set the active variable so the AdjustFramingDetailPanel opens
		// withs the correct variable selected
		dispatch(setActiveVariable(name));
		function Configured(props) {
			let modalProps = {
				...props,
				libraryId,
				versionInfo,
				modalStack,
				saveVersion,
				saveAsVersion,
				commonModalProps: {
					...commonModalProps,
				},

			};

			return <AdjustFramingBlade {...modalProps} />;
		}

		pushModal(Configured);
	};


	function Search() {
		return (
			<td className="edit-version-main" colSpan={2}>
				<Field name={fieldName} {...selectProps} />
			</td>
		);
	}

	function onInputChange(data) {
		dispatch(change(form, fieldName, {
			...value,
			data,
			assetId: null,
			filesize: 0,
		}));
	}

	function Input() {
		const fieldProps = value?.fieldProps || {};
		const label = (
			<>
				<div>{name}</div>
				<div className="text-label-extra">
					<small>{`${value.data?.length || 0} characters`}</small>
				</div>
			</>
		);

		return (
			<td className="edit-version-main editCell" colSpan={2}>
				<Field
					autoFocus
					className="text-asset-field text-asset-input cypress-edit-asset-input"
					component={ReduxControls.Input}
					label={label}
					name={editInputName}
					onChange={onInputChange}
					{...fieldProps}
				/>
			</td>
		);
	}

	function Summary() {
		let displayData = data
			, requiredIcon = undefined;

		if (value?.fieldProps?.required) {
			requiredIcon = value.data ? "fas fa-check" : "fas fa-star";
		}

		if (isVideo) {
			//for display purposes, strip libraryId prefix from video asset, if present
			let libraryIdRegex = new RegExp(`(^${libraryId}\/)(.+)`);
			let match = libraryIdRegex.exec(data);
			//if match is null, the data is not prefixed with a libraryId
			//match[2] is the portion that is just the video asset, without the libraryId or '/'
			if (match && match[2]) {
				displayData = match[2]; //eslint-disable-line prefer-destructuring
			}
		}

		return (
			<>
				<VariableInfoCell
					name={name}
					data={displayData}
					onClick={startSearching}
					requiredIcon={requiredIcon}
				/>
				<td className="controls-cell">
					<div className="buttons">
						{showFilesize && <span className="filesize">{formatBytes(filesize)}</span>}
						{showEditButton && (
							<Tooltip description="Edit" direction="top">
								<Icon className="bounce-circle cypress-edit-asset"
									name="edit"
									onClick={startEditing} />
							</Tooltip>
						)}
						<Tooltip description="Search" direction="top">
							<Icon className="bounce-circle cypress-search"
								name="far fa-search"
								onClick={startSearching} />
						</Tooltip>
						<Tooltip description={`Select ${type}`} direction="top">
							<Icon className="bounce-circle cypress-select-asset"
								name="far fa-cloud"
								onClick={openSelectionModal} />
						</Tooltip>
						<FramingButton isVisible={showAdjustFramingModal} onClick={openAdjustFramingModal} />
					</div>
				</td>
			</>
		);
	}


	let Body = useMemo(() => {
		if (!isEditing) {
			return Summary();
		}
		return isSearching ? Search() : Input();
	}, [isEditing, isSearching, showEditButton, value]);


	return (
		<>
			{ModalStackHead && <ModalStackHead />}
			<tr
				className={classNames("edit-version-table-row", {
					"is-editing": isEditing,
					"text-asset-row": isEditing && !isSearching,
				})}
				ref={rowRef}
			>
				<IconCell iconName={`asset${type}`} onClick={startSearching} />
				{Body}
			</tr>
		</>
	);
}
